import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import useGlobal from 'global-state/store';
import Skeleton from '@mui/material/Skeleton';
import { useFirestore } from 'firebaseHooks/FirestoreContext';
import { useAuth } from 'firebaseHooks/AuthContext';
import {
  doc, getDoc, collection, getDocs,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CreateNewOrganizationButton from './CreateNewOrganizationButton';
import OrganizationInvitationCard from './OrganizationInvitationCard';

// Utility functions for dynamic color generation based on theme
function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h;
  let s;

  if (max === min) {
    h = 0;
    s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
}

function hslToHex(hue, sat, light) {
  const h = hue / 360;
  const s = sat / 100;
  const l = light / 100;

  const hue2rgb = (p, q, t) => {
    let tValue = t;
    if (tValue < 0) tValue += 1;
    if (tValue > 1) tValue -= 1;
    if (tValue < 1 / 6) return p + (q - p) * 6 * tValue;
    if (tValue < 1 / 2) return q;
    if (tValue < 2 / 3) return p + (q - p) * (2 / 3 - tValue) * 6;
    return p;
  };

  let r;
  let g;
  let b;

  if (s === 0) {
    r = l;
    g = l;
    b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function getOrganizationColor(organizationId) {
  // Base theme colors from App.js
  const baseColors = [
    '#437A89', // Primary theme color
    '#73a9b9', // Secondary theme color
    '#99BD5B', // ourGreen from theme
    '#f4ad45', // ourOrange from theme
    '#FFD700', // Gold from theme
    '#3f4d5a', // Text primary from theme
  ];

  // Generate a consistent hash from the organization ID
  let hash = 0;
  for (let i = 0; i < organizationId.length; i += 1) {
    const char = organizationId.charCodeAt(i);
    hash = ((hash * 31) - hash) + char;
    hash = Math.abs(hash);
  }

  // Select a base color based on the hash
  const baseColorIndex = hash % baseColors.length;
  const baseColor = baseColors[baseColorIndex];

  // Convert base color to HSL
  const [baseH, baseS, baseL] = hexToHsl(baseColor);

  // Generate variations by modifying hue, saturation, and lightness
  const hueVariation = (hash % 60) - 30; // ±30 degrees hue variation
  const satVariation = ((Math.floor(hash / 256)) % 40) - 20; // ±20% saturation variation
  const lightVariation = ((Math.floor(hash / 65536)) % 30) - 15; // ±15% lightness variation

  // Apply variations while keeping them within valid ranges
  const newH = Math.max(0, Math.min(360, baseH + hueVariation));
  const newS = Math.max(20, Math.min(80, baseS + satVariation)); // Keep saturation reasonable
  const newL = Math.max(25, Math.min(75, baseL + lightVariation)); // Keep lightness readable

  return hslToHex(newH, newS, newL);
}

export default function Organizations(props) {
  const { t } = useTranslation();
  const [globalState, globalActions] = useGlobal();
  const { currentUser } = useAuth();
  const [loadingState, setloadingState] = useState('initial');
  const db = useFirestore();
  const [orgaInvitations, setorgaInvitations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [organizationsData, setOrganizationsData] = useState({});
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [allOrganizationsLoading, setAllOrganizationsLoading] = useState(false);
  const searchInputRef = useRef(null);

  // Load organization data for search functionality
  useEffect(() => {
    async function loadOrganizationsData() {
      if (globalState.organizations.length > 0) {
        const orgData = {};
        const promises = globalState.organizations.map(async (orgId) => {
          try {
            const orgRef = doc(db, 'organizations', orgId);
            const orgSnap = await getDoc(orgRef);
            if (orgSnap.exists()) {
              orgData[orgId] = orgSnap.data();
            }
          } catch (err) {
            // Silently handle organization loading errors
          }
        });
        await Promise.all(promises);
        setOrganizationsData(orgData);
      }
    }

    if (loadingState === 'loaded' && globalState.organizations.length > 0) {
      loadOrganizationsData();
    }
  }, [db, globalState.organizations, loadingState]);

  useEffect(() => {
    async function loadUser() {
      try {
        const udocRef = doc(db, 'users', currentUser.uid, 'private', 'data');
        const udocSnap = await getDoc(udocRef);
        setorgaInvitations(udocSnap.data()?.organizationInvitations || []);
        setloadingState('loaded');
      } catch (err) {
        globalActions.setSnackbarMessage({ message: `${t('unexpected_error')}: ${err.message}`, severity: 'error' });
      }
    }
    if (loadingState === 'initial' && currentUser?.uid !== undefined) {
      setloadingState('loading');
      loadUser();
    }
  }, [db, globalActions, loadingState, t, currentUser?.uid]);

  // Load all organizations for admin users
  useEffect(() => {
    async function loadAllOrganizations() {
      if (globalState.wsRoles.includes('admin') && loadingState === 'loaded') {
        setAllOrganizationsLoading(true);
        try {
          const orgsRef = collection(db, 'organizations');
          const orgsSnap = await getDocs(orgsRef);
          const allOrgs = orgsSnap.docs.map((docSnapshot) => ({
            id: docSnapshot.id,
            ...docSnapshot.data(),
          }));
          setAllOrganizations(allOrgs);
        } catch (err) {
          globalActions.setSnackbarMessage({ message: `${t('unexpected_error')}: ${err.message}`, severity: 'error' });
        } finally {
          setAllOrganizationsLoading(false);
        }
      }
    }

    loadAllOrganizations();
  }, [db, globalActions, loadingState, t, globalState.wsRoles]);

  const hasOrganizations = globalState.organizations.length > 0;
  const hasInvitations = orgaInvitations.length > 0;
  const showSearchBar = globalState.organizations.length > 4
    || (globalState.wsRoles.includes('admin') && allOrganizations.length > 4);

  // Filter organizations based on search query
  const filteredOrganizations = globalState.organizations.filter((orgId) => {
    if (!searchQuery.trim()) return true;
    const orgData = organizationsData[orgId];
    if (!orgData) return true; // Show if data not loaded yet
    return orgData.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Filter all organizations for admin users (excluding ones they're already part of)
  const filteredAllOrganizations = allOrganizations.filter((org) => {
    // Exclude organizations the user is already part of
    if (globalState.organizations.includes(org.id)) return false;

    if (!searchQuery.trim()) return true;
    return org.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        // Only focus if search bar is visible and ref exists
        if (showSearchBar && searchInputRef.current) {
          // Focus the input
          searchInputRef.current.focus();
          // Select text using the input element inside the TextField
          const inputElement = searchInputRef.current.querySelector('input');
          if (inputElement) {
            inputElement.select();
          }
        }
      }
    };

    // Always add the listener, but only focus if conditions are met
    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSearchBar]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        width: '100%',
        maxWidth: '1200px',
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
        py: 3,
      }}
    >
      {/* Organizations Section */}
      <Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 3,
        }}
        >
          <BusinessIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {t('organizations.mine')}
          </Typography>
        </Box>

        {showSearchBar && (
          <Box sx={{ mb: 3 }}>
            <TextField
              ref={searchInputRef}
              fullWidth
              placeholder={t('organizations.search_placeholder', 'Search organizations... (⌘K)')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <Tooltip title={t('organizations.clear_search', 'Clear search')}>
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery('')}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            {searchQuery && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('organizations.search_results', 'Showing {{count}} of {{total}} organizations', {
                  count: filteredOrganizations.length
                    + (globalState.wsRoles.includes('admin') ? filteredAllOrganizations.length : 0),
                  total: globalState.organizations.length
                    + (globalState.wsRoles.includes('admin') ? allOrganizations.length : 0),
                })}
              </Typography>
            )}
          </Box>
        )}

        {loadingState === 'loading' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton variant="rectangular" height={120} />
            <Skeleton variant="rectangular" height={120} />
          </Box>
        )}

        {loadingState === 'loaded' && (
          <>
            {hasOrganizations && filteredOrganizations.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: filteredOrganizations.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
                    md: filteredOrganizations.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
                  },
                  gap: 3,
                  mb: 3,
                }}
              >
                {filteredOrganizations.map((org) => (
                  <OrganizationCard organizationId={org} key={org} />
                ))}
              </Box>
            )}

            {hasOrganizations && filteredOrganizations.length === 0 && searchQuery && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('organizations.no_search_results', 'No organizations found')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('organizations.try_different_search', 'Try a different search term')}
                </Typography>
              </Box>
            )}

            {!hasOrganizations && (
              <EmptyOrganizationsState
                onCreate={() => globalActions.setUserStatus('initial')}
                isAdmin={globalState.wsRoles.includes('admin')}
              />
            )}

            {hasOrganizations && globalState.wsRoles.includes('admin') && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CreateNewOrganizationButton onCreate={() => globalActions.setUserStatus('initial')} />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Invitations Section */}
      {loadingState === 'loaded' && (
        <Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3,
          }}
          >
            <EmailIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {t('organizations.invitations')}
            </Typography>
          </Box>

          {hasInvitations ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: orgaInvitations.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
                  md: orgaInvitations.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
                },
                gap: 3,
              }}
            >
              {orgaInvitations.map((org) => (
                <OrganizationInvitationCard
                  organizationId={org}
                  userId={currentUser.uid}
                  key={org}
                  decisionCallback={() => setloadingState('initial')}
                />
              ))}
            </Box>
          ) : (
            <EmptyInvitationsState />
          )}
        </Box>
      )}

      {/* All Organizations Section for Admin Users */}
      {loadingState === 'loaded' && globalState.wsRoles.includes('admin') && (
        <Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3,
          }}
          >
            <BusinessIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {t('organizations.all_organizations', 'All Organizations')}
            </Typography>
          </Box>

          {allOrganizationsLoading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Skeleton variant="rectangular" height={120} />
              <Skeleton variant="rectangular" height={120} />
            </Box>
          )}

          {!allOrganizationsLoading && filteredAllOrganizations.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: filteredAllOrganizations.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
                  md: filteredAllOrganizations.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
                },
                gap: 3,
              }}
            >
              {filteredAllOrganizations.map((org) => (
                <AllOrganizationCard organization={org} key={org.id} />
              ))}
            </Box>
          )}

          {!allOrganizationsLoading && filteredAllOrganizations.length === 0 && (
            <EmptyAllOrganizationsState hasSearch={!!searchQuery} />
          )}
        </Box>
      )}
    </Box>
  );
}

// Empty state components
function EmptyOrganizationsState({ onCreate, isAdmin }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Paper
      sx={{
        p: 6,
        textAlign: 'center',
        border: '2px dashed',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        borderRadius: 2,
      }}
    >
      <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        {t('organizations.empty.title', 'No organizations yet')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isAdmin
          ? t('organizations.empty.description', 'Create your first organization to get started')
          : t('organizations.empty.contact_description', 'Contact us to create an organization')}
      </Typography>
      {isAdmin ? (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreate}
          size="large"
        >
          {t('organizations.create_one')}
        </Button>
      ) : (
        <Button
          variant="outlined"
          onClick={() => navigate('/contact')}
          size="large"
        >
          {t('organizations.empty.contact_us')}
        </Button>
      )}
    </Paper>
  );
}

function EmptyInvitationsState() {
  const { t } = useTranslation();

  return (
    <Paper
      sx={{
        p: 4,
        textAlign: 'center',
        border: '2px dashed',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        borderRadius: 2,
      }}
    >
      <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
      <Typography variant="body1" color="text.secondary">
        {t('organizations.invitations_empty')}
      </Typography>
    </Paper>
  );
}

function EmptyAllOrganizationsState({ hasSearch }) {
  const { t } = useTranslation();

  return (
    <Paper
      sx={{
        p: 4,
        textAlign: 'center',
        border: '2px dashed',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        borderRadius: 2,
      }}
    >
      <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
      <Typography variant="body1" color="text.secondary">
        {hasSearch
          ? t('organizations.all_organizations.no_search_results', 'No other organizations found matching your search')
          : t('organizations.all_organizations.empty', 'You are a member of all organizations in the system')}
      </Typography>
    </Paper>
  );
}

function AllOrganizationCard({ organization }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Paper
      onClick={() => {
        navigate('organization', {
          state: {
            organization,
          },
        });
      }}
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        gap: 2,
        minHeight: 120,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: (theme) => theme.shadows[8],
        },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'background.paper',
        opacity: 0.8, // Slightly dimmed to indicate it's not the user's organization
      }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        gap: 1,
      }}
      >
        <BusinessIcon sx={{ fontSize: 40, color: getOrganizationColor(organization.id), mb: 1 }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            textAlign: 'center',
            wordBreak: 'break-word',
          }}
        >
          {organization.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center' }}
        >
          {t('organizations.click_to_inspect', 'Click to inspect')}
        </Typography>
      </Box>
    </Paper>
  );
}

function OrganizationCard(props) {
  const { organizationId } = props;
  const { t } = useTranslation();
  const [loadingState, setloadingState] = useState('initial');
  const [organization, setorganization] = useState(undefined);
  const db = useFirestore();
  const navigate = useNavigate();

  useEffect(() => {
    async function loadOrga() {
      const orgRef = doc(db, 'organizations', organizationId);
      const orgSnap = await getDoc(orgRef);
      setorganization(orgSnap.data());
      setloadingState('loaded');
    }
    if (loadingState === 'initial') {
      setloadingState('loading');
      loadOrga();
    }
  }, [db, loadingState, organizationId]);

  return (
    <Paper
      onClick={() => {
        navigate('organization', {
          state: {
            organization: { ...organization, id: organizationId },
          },
        });
      }}
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        gap: 2,
        minHeight: 120,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: (theme) => theme.shadows[8],
        },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      {loadingState === 'loading' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="text" width="80%" height={32} />
        </Box>
      )}

      {loadingState === 'loaded' && organization && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          gap: 1,
        }}
        >
          <BusinessIcon sx={{ fontSize: 40, color: getOrganizationColor(organizationId), mb: 1 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
              wordBreak: 'break-word',
            }}
          >
            {organization.name}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            {t('organizations.click_to_access', 'Click to access')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
